import { monthKeyFromT } from '../engine/calendar';
import { emptyInputs } from '../engine/evaluate';
import type { IgnoreGroup } from '../engine/ignoreGroups';
import {
    allowedDrivers,
    indexSchema,
    loadSchema,
} from '../engine/schema';
import type { CaseInputs, History, SchemaEntry, View } from '../engine/types';
import { LOCATION_VIEWS } from '../engine/types';
import type { CaseFileCashPolicy, CaseFileLine, CaseFileV1 } from './types';
import { FILE_VIEWS } from './types';

export function lastClosedDateFromFileMonth(yyyyMm: string): string {
    const match = /^(\d{4})-(\d{2})$/.exec(yyyyMm.trim());
    if (!match) {
        return yyyyMm;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = new Date(year, month, 0).getDate();
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function fileMonthFromLastClosedDate(lastClosedDate: string): string {
    const part = lastClosedDate.trim().split('T')[0] ?? lastClosedDate;
    const [y, m] = part.split('-');
    return `${y}-${m}`;
}

export function projectionMonthKeys(lastClosedDate: string): string[] {
    const months: string[] = [];
    for (let t = 1; t <= 36; t += 1) {
        months.push(monthKeyFromT(lastClosedDate, t));
    }
    return months;
}

export function monthIndex(yyyyMm: string): number {
    const [yStr, mStr] = yyyyMm.split('-');
    const y = Number(yStr);
    const m = Number(mStr);
    return y * 12 + (m - 1);
}

export function blankCashPolicy(): CaseFileCashPolicy {
    return {
        enabled: false,
        floor: { operating_minimum: null, drawdown_allowance: null },
        distribution: { cadence: null, anchor_date: null, amount_per_period: null },
        sweep: {
            enabled: false,
            mode: 'excess',
            amount_per_period: null,
            yield_income_view: null,
            opening_mm_balance: null,
        },
        solver: { horizon_months: 12, round_to: 500 },
    };
}

export function trimSchemaLabel(label: string): string {
    return label.trim();
}

export function lineDisplayName(schema: SchemaEntry[], lineKey: string): string {
    const entry = schema.find((row) => row.key === lineKey);
    const label = entry ? trimSchemaLabel(entry.label) : lineKey;
    return `${label} (${lineKey})`;
}

export function hasNonCarryForwardDriver(
    schema: SchemaEntry[],
    lineKey: string,
    view: View,
    consolidatedOnly: string[] = [],
    inputs?: CaseInputs,
): boolean {
    return allowedDrivers(schema, lineKey, view, consolidatedOnly, inputs).some(
        (driver) => driver !== 'carry_forward',
    );
}

export function ignoreGroupInputsStub(group: IgnoreGroup, view: View): CaseInputs {
    const inputs = emptyInputs();
    if (!inputs.drivers[view]) {
        inputs.drivers[view] = {};
    }
    for (const member of group.members) {
        inputs.drivers[view]![member] = { active: 'ignore', by_type: {} };
    }
    return inputs;
}

export function viewsForIgnoreGroupParent(schema: SchemaEntry[], parentKey: string): View[] {
    const entry = schema.find((row) => row.key === parentKey);
    if (!entry) {
        return [];
    }
    return entry.tab === 'IS' ? [...LOCATION_VIEWS] : ['consolidated'];
}

export function collectTemplateLineKeys(
    schema: SchemaEntry[],
    groups: IgnoreGroup[],
): Array<{ view: View; lineKey: string; ignoreMembers?: string[] }> {
    const seen = new Set<string>();
    const lines: Array<{ view: View; lineKey: string; ignoreMembers?: string[] }> = [];

    const add = (view: View, lineKey: string, ignoreMembers?: string[]) => {
        const id = `${view}:${lineKey}`;
        if (seen.has(id)) {
            return;
        }
        seen.add(id);
        lines.push({ view, lineKey, ignoreMembers });
    };

    for (const entry of schema) {
        for (const view of FILE_VIEWS) {
            if (hasNonCarryForwardDriver(schema, entry.key, view)) {
                add(view, entry.key);
            }
        }
    }

    for (const group of groups) {
        for (const view of viewsForIgnoreGroupParent(schema, group.parent)) {
            const stub = ignoreGroupInputsStub(group, view);
            if (hasNonCarryForwardDriver(schema, group.parent, view, [], stub)) {
                add(view, group.parent, group.members);
            }
        }
    }

    const keyOrder = new Map(schema.map((entry, index) => [entry.key, index]));
    lines.sort(
        (a, b) =>
            (keyOrder.get(a.lineKey) ?? 9999) - (keyOrder.get(b.lineKey) ?? 9999) ||
            FILE_VIEWS.indexOf(a.view) - FILE_VIEWS.indexOf(b.view),
    );

    return lines;
}

export function buildCaseFileLine(
    schema: SchemaEntry[],
    lineKey: string,
    view: View,
    consolidatedOnly: string[],
    inputs?: CaseInputs,
    ignoreMembers?: string[],
    history?: History,
    historyMonths = 24,
    lastClosedDate?: string,
): CaseFileLine {
    const entry = schema.find((row) => row.key === lineKey)!;
    const allowed = allowedDrivers(schema, lineKey, view, consolidatedOnly, inputs);
    const line: CaseFileLine = {
        label: trimSchemaLabel(entry.label),
        unit: entry.unit,
        allowed_drivers: [...allowed],
        driver: null,
        values: Array(36).fill(null),
        params: {},
    };
    if (ignoreMembers) {
        line.ignore_group_members = [...ignoreMembers];
    }
    if (history && lastClosedDate) {
        const raw = history.values[view]?.[lineKey] ?? [];
        const months: string[] = [];
        const values: (number | null)[] = [];
        const startT = -(historyMonths - 1);
        for (let t = startT; t <= 0; t += 1) {
            months.push(monthKeyFromT(lastClosedDate, t));
            const idx = history.nHistory - 1 + t;
            const value = raw[idx];
            values.push(typeof value === 'number' && Number.isFinite(value) ? value : null);
        }
        line.history = { months, values };
    }
    return line;
}

export const REFERENCE_SUBTOTAL_KEYS = [
    'is.gross_product_sales',
    'is.net_revenue',
    'is.total_cogs',
    'is.gross_profit',
    'is.total_operating_expense',
    'is.net_income',
] as const;

export const REFERENCE_CONSOLIDATED_BS_KEYS = [
    'bs.total_cash_and_equivalents',
    'bs.total_assets',
    'bs.total_liabilities',
    'bs.total_equity',
] as const;

export function buildReferenceBlock(
    schema: SchemaEntry[],
    history: History,
    historyMonths: number,
    lastClosedDate: string,
): Record<string, Record<string, { months: string[]; values: (number | null)[] }>> {
    const { byKey } = indexSchema(schema);
    const out: Record<string, Record<string, { months: string[]; values: (number | null)[] }>> = {};
    for (const view of FILE_VIEWS) {
        const keys =
            view === 'consolidated'
                ? [...REFERENCE_SUBTOTAL_KEYS, ...REFERENCE_CONSOLIDATED_BS_KEYS]
                : [...REFERENCE_SUBTOTAL_KEYS];
        out[view] = {};
        for (const key of keys) {
            if (!byKey[key]) {
                continue;
            }
            const line = buildCaseFileLine(
                schema,
                key,
                view,
                [],
                undefined,
                undefined,
                history,
                historyMonths,
                lastClosedDate,
            );
            if (line.history) {
                out[view][key] = line.history;
            }
        }
    }
    return out;
}

export function emptyLinesRecord(): CaseFileV1['lines'] {
    return {
        river_oaks: {},
        highland_park: {},
        ga: {},
        consolidated: {},
    };
}

export function isBlankCashPolicyFile(policy: CaseFileCashPolicy): boolean {
    return (
        !policy.enabled &&
        policy.distribution.cadence == null &&
        policy.distribution.anchor_date == null
    );
}

export function normalizeInputsForCompare(inputs: CaseInputs): CaseInputs {
    const drivers: CaseInputs['drivers'] = {};
    for (const [view, lines] of Object.entries(inputs.drivers)) {
        drivers[view] = {};
        for (const [lineKey, state] of Object.entries(lines ?? {})) {
            if (!state.active) {
                continue;
            }
            const active = state.active;
            const cache = state.by_type[active];
            drivers[view]![lineKey] = {
                active,
                by_type: cache ? { [active]: { ...cache } } : {},
            };
        }
    }
    const cashPolicy = inputs.cash_policy
        ? {
              ...inputs.cash_policy,
              distribution: {
                  ...inputs.cash_policy.distribution,
                  split_schedule: null,
              },
              sweep: {
                  ...inputs.cash_policy.sweep,
                  yield_snapshot: null,
              },
          }
        : null;
    return {
        consolidated_only: [...inputs.consolidated_only],
        drivers,
        one_time_items: inputs.one_time_items.map((item) => ({ ...item })),
        cash_policy: cashPolicy,
    };
}

export { loadSchema };
